import { AddIcon, CloseIcon } from '@chakra-ui/icons';
import {
  Box,
  Checkbox,
  CheckboxGroup,
  FormLabel,
  Grid,
  Heading,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  SimpleGrid,
  Stack,
  Tag,
  TagRightIcon,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Permission, Role } from 'api/types';
import useAllPermissionsQuery from 'hooks/useAllPermissionsQuery';
import { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { IUserForm } from './UserForm';

type Props = {
  roleOptions: Role[];
  mode: 'create' | 'update';
};

function UserRolesAndPermissions({ roleOptions, mode }: Props) {
  const { t } = useTranslation('user');
  const {
    setValue,
    watch,
    formState: { errors },
    trigger,
  } = useFormContext<IUserForm>();
  const allPermissionsQuery = useAllPermissionsQuery();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const allPermissions = allPermissionsQuery.data ?? [];
  const [search, setSearch] = useState('');

  const selectedUserRoles = watch('roles');
  const permissionsAssignedByRole = useMemo<Permission[]>(
    () =>
      selectedUserRoles
        .filter((userRole) => roleOptions.some((roleOption) => userRole.name === roleOption.name))
        .flatMap((role) => role.permissions)
        .map((permission) => ({ ...permission, description: '' })),
    [selectedUserRoles, roleOptions],
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const selectedPermissionNames = watch('permissionNames') ?? [];
  const selectedPermissions = useMemo<Permission[]>(
    () => allPermissions.filter((permission) => selectedPermissionNames.includes(permission.name)),
    [allPermissions, selectedPermissionNames],
  );
  const availablePermissions = useMemo(
    () =>
      allPermissions.filter(
        (permission) =>
          !permissionsAssignedByRole.some((p) => p.name === permission.name) && !selectedPermissions.some((p) => p.name === permission.name),
      ),
    [allPermissions, permissionsAssignedByRole, selectedPermissions],
  );
  const searchedAvailablePermissions = useMemo(
    () => availablePermissions.filter((permissions) => permissions.label.toLowerCase().includes(search.toLowerCase().trim())),
    [availablePermissions, search],
  );

  useEffect(() => {
    // Remove all selected permissions which are alreay contained in a selected role
    setValue(
      'permissionNames',
      selectedPermissionNames.filter((name) => !permissionsAssignedByRole.some((permission) => permission.name === name)),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUserRoles]);

  return (
    <Stack as="section" spacing={3}>
      <Heading size="xxs">{mode === 'create' ? t('roles_and_permissions.headingCreate') : t('roles_and_permissions.headingUpdate')}</Heading>
      <Stack mt={1} spacing={1}>
        <FormLabel>{t('roles_and_permissions.roles.heading')}</FormLabel>
        <Grid display={'flex'} flexDirection={'row'} flexWrap={'wrap'}>
          <CheckboxGroup defaultValue={watch('roles').map((role) => role.name)}>
            {roleOptions.map((roleOption) => (
              <Checkbox
                key={roleOption.name}
                value={roleOption.name}
                size={'md'}
                onChange={(e) => {
                  const newRoles = selectedUserRoles.filter((selectedRole) => selectedRole.name !== roleOption.name);
                  if (e.target.checked) {
                    newRoles.push(roleOption);
                  }
                  setValue('roles', newRoles);
                  trigger('roles');
                }}
              >
                <Text color="muted" marginRight={6}>
                  {roleOption.label}
                </Text>
              </Checkbox>
            ))}
          </CheckboxGroup>
        </Grid>
        <Text color="error" fontSize="sm">
          {errors.roles?.message}
        </Text>
      </Stack>

      {mode === 'update' && (
        <>
          <FormLabel>{t('roles_and_permissions.permissions.heading')}</FormLabel>
          <SimpleGrid columns={{ lg: 2, base: 1 }} borderWidth="1px" borderRadius="lg" p="3" spacing={6}>
            <Box>
              <FormLabel fontSize="xs" fontWeight={'600'} textTransform="uppercase" color="gray.700">
                {t('roles_and_permissions.permissions.available')}
              </FormLabel>
              <InputGroup size="sm">
                <Input placeholder={t('roles_and_permissions.search.placeholder')} value={search} onChange={(e) => setSearch(e.target.value)} />
                <InputRightElement>
                  <IconButton size="xs" onClick={() => setSearch('')} aria-label={t('common:close')}>
                    {<CloseIcon boxSize={2} />}
                  </IconButton>
                </InputRightElement>
              </InputGroup>

              <VStack height={{ lg: '400px', base: 'auto' }} overflowY="auto" pr="2" mt="4" spacing="2" align={'start'}>
                {searchedAvailablePermissions.map((permission) => {
                  return (
                    <Tag
                      key={permission.name}
                      size="lg"
                      cursor="pointer"
                      bg={'gray.500'}
                      color={'white'}
                      _hover={{ bg: 'gray.600' }}
                      onClick={() => {
                        setValue('permissionNames', [...selectedPermissionNames, permission.name]);
                      }}
                    >
                      <HighlightedText text={permission.label} highlight={search.toLowerCase().trim()} />
                      <TagRightIcon as={AddIcon} boxSize="4" color="gray.200" />
                    </Tag>
                  );
                })}
              </VStack>
            </Box>
            <Box>
              <FormLabel fontSize="xs" fontWeight={'600'} textTransform="uppercase" color="gray.700">
                {t('roles_and_permissions.permissions.assigned')}
              </FormLabel>

              <VStack height={{ lg: '400px', base: 'auto' }} overflowY="auto" pr="2" mt={{ lg: '12', base: '4' }} spacing="2" align={'start'}>
                {selectedPermissions.map((permission) => {
                  return (
                    <Tag
                      key={permission.name}
                      size="lg"
                      cursor="pointer"
                      bg={'blue.500'}
                      color={'white'}
                      _hover={{ bg: 'blue.700' }}
                      onClick={() => {
                        setValue(
                          'permissionNames',
                          selectedPermissionNames.filter((currentPermission) => currentPermission !== permission.name),
                        );
                      }}
                    >
                      {permission.label}
                      <TagRightIcon as={CloseIcon} boxSize="3" color={'blue.300'} />
                    </Tag>
                  );
                })}
                {permissionsAssignedByRole.map((permission, index) => {
                  return (
                    <Tag key={index} size="lg" color="blue.900">
                      {permission.label}
                    </Tag>
                  );
                })}
              </VStack>
            </Box>
          </SimpleGrid>
        </>
      )}
    </Stack>
  );
}

export default UserRolesAndPermissions;

type HighlightedTextProps = {
  text: string;
  highlight: string;
};
function HighlightedText({ text, highlight }: HighlightedTextProps) {
  // Split on highlight term and include term into parts, ignore case
  const parts = text.split(new RegExp(`(${highlight})`, 'i'));
  return (
    <span>
      {' '}
      {parts.map((part, i) => (
        <span key={i} style={part.toLowerCase() === highlight.toLowerCase() ? { fontWeight: 'bold' } : {}}>
          {part}
        </span>
      ))}{' '}
    </span>
  );
}
